import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { AppComponent } from '../app.component';
import { HubService } from '../services/hub.service';
import { FirmwareDetails } from '../types/firmware-details';
import { FirmwareRoute } from '../types/firmware-route';
import { FirmwareSet } from '../types/firmware-set';

@Component({
  selector: 'firmware-set',
  template: `<ng-container *ngIf="!!firmwareSet.target_firmware_uid; else no_firmware">
  <span class="version">{{ firmwareSet.major }}.{{ firmwareSet.minor }}.{{ firmwareSet.patch }}</span>
</ng-container>
<ng-template #no_firmware>
  <span class="version">-</span>
</ng-template>
<span class="comment">
  <mat-form-field class="comment" style="width:15em; padding-left: 2em;">
    <mat-select placeholder="Change default firmware version" [(ngModel)]="firmwareSet.target_firmware_uid" (ngModelChange)="changed()">
      <mat-option *ngFor="let firmware of firmwares" [value]="firmware.uid">
        {{firmware.major}}.{{firmware.minor}}.{{firmware.patch}}<ng-container *ngIf="firmware.name"> - {{firmware.name}}</ng-container>
      </mat-option>
    </mat-select>
  </mat-form-field>
</span>
<span class="panel">
  <button mat-raised-button (click)="cancel()">Cancel</button>
  <button mat-raised-button color="primary" (click)="save()" [disabled]="!hasChanges">Save</button>
</span>
<ul class="firmware-routes">
  <li>
    <span class="id">UID</span>
    <span class="comment">Comment</span>
    <span class="bool center">Force</span>
    <span class="comment">Eligibility for Upgrade</span>
    <span class="actions">
      <mat-icon class="md-48" color="primary" aria-label="Add a new row" (click)="addFirmwareRoute()">add_outline</mat-icon>
    </span>
  </li>
  <li *ngIf="editRoute && editRoute.uid < 0">
    <firmware-route-edit
      [inRoute]="editRoute"
      [firmwares]="firmwares"
      (routeSaved)="onRouteSaved($event)"></firmware-route-edit>
  </li>
  <li *ngFor="let firmwareroute of routes" class="firmware-set">
    <ng-container *ngIf="editRoute && editRoute.uid == firmwareroute.uid; else displayroute">
      <firmware-route-edit
        [inRoute]="editRoute"
        [firmwares]="firmwares"
        (routeSaved)="onRouteSaved($event)"></firmware-route-edit>
    </ng-container>
    <ng-template #displayroute>
      <span class="id">{{ firmwareroute.uid }}</span>
      <span class="comment">{{ firmwareroute.comment }}</span>
      <span class="bool center">{{ firmwareroute.force | boolean }}</span>
      <span class="comment">
        <ng-container *ngIf="firmwareroute.firmwareid">Firmware:
          {{firmwareroute.source_major}}.{{firmwareroute.source_minor}}.{{firmwareroute.source_patch}}
        </ng-container>
        <ng-container *ngIf="firmwareroute.hubid">Hub: {{firmwareroute.hubid}}</ng-container>
        <ng-container *ngIf="firmwareroute.ip">Network: {{firmwareroute.ip}}</ng-container>
      </span>
      <span class="actions">
        <mat-icon class="md-48" color="primary" aria-label="Add a new row" (click)="editFirmwareRoute(firmwareroute)">edit</mat-icon>
        <mat-icon class="md-48" color="warn" aria-label="Delete this row" (click)="deleteFirmwareRoute(firmwareroute.uid)">clear</mat-icon>
      </span>
    </ng-template>
  </li>
</ul>`,
  styles: [
    `li { display: block; padding: 0 1em; vertical-align: middle; height: fit-content;}`,
    `li span { display: inline-block; margin: 0.1em; padding: 0.1em 0.63em; line-height: 2.4em;}`,
    `li span.id { width: 4em; color: white; background-color: #607D8B; margin: 0; line-height: 2.6em; }`,
    `.comment { width: 18em; }`,
    `.bool { width: 2em; text-align: center; }`,
    `.number { width: 5.5em; text-align: right; }`,
    `.ip { width: 8.5em; }`,
    `.version { width: 5em; text-align: center; }`,
    `li span.actions { vertical-align: middle; width: fit-content; line-height: 1.8em}`,
    `span.actions mat-icon { margin: 0.2em;}`,
    `button { margin: 0 0.5em; }`,
  ],
  providers: [HubService]
})

export class FirmwareSetComponent implements OnInit {
  @Input() inFirmwareSet: FirmwareSet;
  @Input() firmwares: FirmwareDetails[];
  @Output() modsCompleted = new EventEmitter<boolean>();
  errorMessage: string;
  routes: FirmwareRoute[];
  hasChanges = false;
  firmwareSet: FirmwareSet;
  editRoute: FirmwareRoute;

  constructor(
    private hubService: HubService
  ) {
  }

  ngOnInit(): void {
    if (!AppComponent.IsLoggedIn()) { return; }
    if (this.inFirmwareSet) {
      this.firmwareSet = this.inFirmwareSet;
      this.refresh();
    } else {
      this.firmwareSet = <FirmwareSet>{};
    }
  }

  refresh(): void {
    this.hubService.getFirmwareRoutes(this.firmwareSet.fwsetid)
      .subscribe(
        routes => this.updateRoutes(this.firmwareSet.fwsetid, routes),
        err => HubService.LogRefreshError(err)
      );
  }

  updateRoutes(fwsetid: number, newRoutes: Array<FirmwareRoute>): void {
    this.routes = newRoutes;
  }

  changed() {
    this.hasChanges = true;
  }

  cancel(): void {
    this.modsCompleted.emit(false);
  }

  save(): void {
    this.hubService.updateFirmwareSet(this.firmwareSet)
      .subscribe(
        () => {
          this.hasChanges = false;
          this.modsCompleted.emit(true);
        },
        e => this.errorMessage = e
      );
  }

  addFirmwareRoute(): void {
    this.editRoute = <FirmwareRoute>{
      uid: -1,
      fwsetid: this.firmwareSet.fwsetid,
    };
  }

  editFirmwareRoute(editMe: FirmwareRoute): void {
    this.editRoute = editMe;
  }

  onRouteSaved(event: Event): void {
    this.refresh();
    this.editRoute = undefined;
  }

  deleteFirmwareRoute(deleteMe: number): void {
    this.hubService.deleteFirmwareRoute(deleteMe)
      .subscribe(
        () => this.modsCompleted.emit(true),
        err => HubService.LogRefreshError(err),
      );
  }
}
